import {
  authHeader,
  buildEnvelope,
  parseDsn,
  type ErrorReportOptions,
  type ParsedDsn,
} from '@avslack/shared';
import { apiBase } from './runtime';

/**
 * Hibajelentes a bongeszobol.
 *
 * A cim FUTASIDOBEN erkezik a szervertol, nem a buildbe egetve: igy egy
 * kornyezeti valtozo atirasa eleg, es nem kell ujra forditani a feluletet -
 * amit az asztali app ugyis a szerverrol tolt.
 *
 * Amig a beallitas meg nem erkezett, a hibakat SORBA ALLITJUK. A betoltes
 * elso masodperceiben tortent hiba a legerdekesebb; ha eldobnank, pont azt
 * nem latnank, ami miatt valakinek el sem indul az alkalmazas.
 */

let target: ParsedDsn | null = null;
let options: ErrorReportOptions | null = null;
const pending: { error: unknown; where: string }[] = [];

/** Ennyi hibat orzunk meg a beallitas megerkezeseig. */
const MAX_PENDING = 20;

/**
 * Ugyanaz a hiba egy hurokban ezerszer is elsulhet.
 *
 * A `unhandledrejection` egy ujraprobalkozo ciklusban masodpercenkent tobbszor
 * is jon. Beküldés nelkuli szures nelkul ez percek alatt elarasztana a
 * szolgaltatast - es a valodi, ritka hibak elvesznenek benne.
 */
const seen = new Map<string, number>();
const REPEAT_WINDOW_MS = 60_000;

function tooOften(key: string): boolean {
  const now = Date.now();
  const last = seen.get(key);
  if (last !== undefined && now - last < REPEAT_WINDOW_MS) return true;
  seen.set(key, now);
  return false;
}

function send(error: unknown, where: string): void {
  const key = `${where}|${error instanceof Error ? error.message : String(error)}`;
  if (tooOften(key)) return;

  if (!target || !options) {
    if (pending.length < MAX_PENDING) pending.push({ error, where });
    return;
  }
  void deliver(error, where);
}

/** Kezzel jelentett hiba - ott, ahol a kod maga tudja, hogy baj van. */
export function reportError(error: unknown, where: string): void {
  send(error, where);
}

export async function initErrorReporting(): Promise<void> {
  /*
    A figyelok AZONNAL felkerulnek, meg a beallitas lekerese ELOTT.

    A lekeres halozati keres; ha megvarnank, a betoltes elso masodperceiben
    tortent hibakrol - eppen a legerdekesebbekrol - lemaradnank.
  */
  window.addEventListener('error', (event) => {
    send(event.error ?? new Error(event.message), 'window.error');
  });

  window.addEventListener('unhandledrejection', (event) => {
    send(event.reason, 'window.unhandledrejection');
  });

  try {
    const response = await fetch(`${apiBase()}/api/client-config`, { credentials: 'include' });
    if (!response.ok) return;

    const config = (await response.json()) as {
      errorReporting?: { dsn?: string; environment?: string };
    };
    const dsn = config.errorReporting?.dsn;
    if (!dsn) return;

    target = parseDsn(dsn);
    options = {
      environment: config.errorReporting?.environment ?? 'production',
      platform: 'javascript',
      serverName: 'avslack-felulet',
    };

    for (const item of pending.splice(0)) void deliver(item.error, item.where);
  } catch {
    // A hibajelentes hianya nem torheti el az alkalmazast.
  }
}

/**
 * A tenyleges kuldes.
 *
 * SOSEM dob: a hibajelentes hibaja nem ronthatja el azt, ami amugy is eppen
 * rosszul megy - es a felhasznalo szamara lathatatlan kell maradjon.
 */
async function deliver(error: unknown, where: string): Promise<void> {
  if (!target || !options) return;

  try {
    await fetch(target.url, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-sentry-envelope',
        'x-sentry-auth': authHeader(target.key),
      },
      body: buildEnvelope({
        eventId: crypto.randomUUID().replace(/-/g, ''),
        sentAt: new Date().toISOString(),
        error,
        options,
        context: { where },
      }),
    });
  } catch {
    // Halozat nelkul nincs mit tenni.
  }
}
